/**
 * EL AVISO DE UNA COPIA DE GALERÍA. Módulo puro: sin React, sin red.
 *
 * El runner de la web corre en `node` sin jsdom (`vitest.config.ts`), así que toda regla que se
 * pueda decidir sin un DOM vive acá y se prueba — el hook se queda con llamar al servidor.
 *
 * ── ☠️ POR QUÉ EL AVISO CUENTA CUATRO COSAS Y NO DICE «LISTO» ───────────────
 *
 * Porque una copia hace hasta cuatro cosas a la vez y tres de ellas son invisibles en la pantalla
 * que uno estaba mirando:
 *
 * | | |
 * |---|---|
 * | `copied` | Filas nuevas. Es lo único que el usuario pidió explícitamente |
 * | `skipped` | Las que el destino YA tenía sobre esos bytes. Sin decirlo, «copié 5» contra una galería que sumó 2 se lee como un bug |
 * | `detached` | Lo que el `replace` sacó del slot. Es trabajo de curaduría que alguien había hecho: callarlo es lo peor que puede hacer este aviso |
 * | `skippedByLimit` | Las que no entraron por el tope. «Se copiaron 15» cuando entraron 12 es una mentira que se descubre en el teléfono de un cliente |
 *
 * El orden de las frases es el de la importancia para quien mira: primero lo que entró, después lo
 * que no, y al final lo que salió.
 */

/** Exactamente lo que devuelve `POST /v2/admin/documents/copy-gallery`, en lo que acá se usa. */
export type CopyGalleryOutcome = {
  copied: number
  skipped: number
  detached: number
  skippedByLimit: number
}

/** `1 foto` / `N fotos`. Se escribe una vez para que las cuatro frases concuerden igual. */
function fotos(count: number): string {
  return count === 1 ? '1 foto' : `${count} fotos`
}

export function copyGalleryNotice(outcome: CopyGalleryOutcome): string {
  const partes: string[] = []

  if (outcome.copied > 0) {
    partes.push(`Se copiaron ${fotos(outcome.copied)}.`)
  }

  if (outcome.skipped > 0) {
    partes.push(
      outcome.skipped === 1
        ? '1 ya estaba en esta galería.'
        : `${outcome.skipped} ya estaban en esta galería.`
    )
  }

  if (outcome.skippedByLimit > 0) {
    partes.push(
      outcome.skippedByLimit === 1
        ? 'Una no entró: la galería admite 12.'
        : `${outcome.skippedByLimit} no entraron: la galería admite 12.`
    )
  }

  if (outcome.detached > 0) {
    // «Salieron», no «se borraron»: el archivo sigue en el cartapacio y se puede volver a poner.
    // La palabra importa — quien lee «se borraron» va a buscar cómo recuperarlas.
    partes.push(
      outcome.detached === 1
        ? '1 foto propia salió de la galería; el archivo sigue en el cartapacio.'
        : `${outcome.detached} fotos propias salieron de la galería; los archivos siguen en el cartapacio.`
    )
  }

  // Nada cambió. Es un resultado legítimo —copiar lo que ya estaba— y merece una frase afirmativa:
  // un toast de éxito vacío deja sin saber si la llamada salió o se perdió.
  if (partes.length === 0) {
    return 'La galería ya tenía todas esas fotos: no cambió nada.'
  }

  // Quedan internas hasta que alguien las publique, salvo que ya hayan entrado publicadas — y eso
  // no se sabe desde acá. Se dice lo que SIEMPRE es verdad: revisá el estado de cada una.
  if (outcome.copied > 0) {
    partes.push('Revisa cuáles están publicadas: el cliente solo ve esas.')
  }

  return partes.join(' ')
}
